
/**
 * SDK Integration Tab Help Content
 */
const SDKHelp = () => {
  return (
    <>
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Aivilo SDK
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The Aivilo SDK provides full observability for your AI agents — automatic telemetry capture, performance monitoring, memory tracking, PII redaction, and prompt/dataset management.
        </p>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Quick Start
        </h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>1. Create an API key in the <span className="font-medium">API Keys</span> tab</p>
          <p>2. Install: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">pip install aivilo</code></p>
          <p>3. Set environment variables: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">AIVILO_API_KEY</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">AIVILO_BASE_URL</code></p>
          <p>4. Wrap your agent or start sending telemetry</p>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Core Components
        </h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&#8226; <span className="font-medium">AiviloAgent</span>: Wraps Strands agents with automatic telemetry, memory tracking, APM, and incident detection</p>
          <p>&#8226; <span className="font-medium">AiviloTracer</span>: OpenTelemetry integration with GenAIOps attribute injection and Bedrock prompt tracking</p>
          <p>&#8226; <span className="font-medium">AiviloAPM</span>: Background performance monitoring — CPU, memory, threads</p>
          <p>&#8226; <span className="font-medium">AiviloClient</span>: Fetch prompts, datasets, and run server-side evaluations</p>
          <p>&#8226; <span className="font-medium">PIIRedactor</span>: Detect and redact sensitive data (emails, SSN, credit cards, API keys)</p>
          <p>&#8226; <span className="font-medium">TestRunner</span>: Run local evaluations against datasets with scoring and quality gates</p>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          AiviloAgent
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
          The simplest way to add observability to a Strands agent. Wraps your agent transparently and captures everything automatically:
        </p>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&#8226; Conversation memory tracking with token estimation</p>
          <p>&#8226; Background APM metric collection</p>
          <p>&#8226; Automatic incident detection and error classification</p>
          <p>&#8226; All telemetry flows to Agent Ops dashboards</p>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          AiviloTracer
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
          Fine-grained OpenTelemetry tracing for custom instrumentation:
        </p>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&#8226; <span className="font-medium">span()</span>: Create spans with auto-injected prompt/dataset attributes</p>
          <p>&#8226; <span className="font-medium">bedrock_span()</span>: Track Bedrock calls with prompt ARN and model ID</p>
          <p>&#8226; <span className="font-medium">memory_span()</span>: Track memory operations (conversation, working, tool_cache)</p>
          <p>&#8226; Content capture policies: capture, redact, or hash</p>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Prompt Labels
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
          Configure your agent to always fetch the "stable" version of a prompt. When a rollback happens, the label moves automatically and your agent picks up the change without redeployment.
        </p>
        <div className="bg-gray-100 dark:bg-gray-800 rounded p-2 mb-3">
          <code className="text-xs text-gray-700 dark:text-gray-300">
            GET /prompts/resolve?name=my-prompt&environment=production&label=stable
          </code>
        </div>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&#8226; Returns the resolved version number and Bedrock ARN</p>
          <p>&#8226; Labels sync to Bedrock as tags (<code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">aivilo:label=stable</code>)</p>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          Configuration
        </h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>&#8226; <span className="font-medium">AIVILO_API_KEY</span>: API key for authentication</p>
          <p>&#8226; <span className="font-medium">AIVILO_BASE_URL</span>: Backend URL</p>
          <p>&#8226; <span className="font-medium">AIVILO_CAPTURE_CONTENT</span>: Content capture policy</p>
          <p>&#8226; <span className="font-medium">AIVILO_REDACT_PII</span>: Enable PII redaction</p>
          <p>&#8226; <span className="font-medium">Config file</span>: ~/.aivilo/config.yaml (per-environment settings)</p>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-500">
          View the complete SDK documentation with code examples in this tab.
        </p>
      </div>
    </>
  );
};

export default SDKHelp;
